import React, { useState, useEffect } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonList, IonItem,
  IonLabel, IonButton, IonIcon, IonCard, IonCardHeader, IonCardTitle,
  IonCardContent, IonBadge, IonChip, IonSegment, IonSegmentButton,
  IonModal, IonInput, IonSelect, IonSelectOption, IonToggle, IonRange,
  IonItemDivider, IonButtons, IonGrid, IonRow, IonCol, IonRefresher,
  IonRefresherContent, RefresherEventDetail, IonActionSheet,
} from '@ionic/react';
import {
  tv, power, sunny, moon, refresh, settings, apps, eye, eyeOff,
  volumeHigh, alertCircle, images, home, cafe, bed, sparkles,
} from 'ionicons/icons';
import {
  getAllScreens, subscribeToScreenStatus, sendCommandToAll,
  changeScreenMode, wakeScreen, sleepScreen, setScreenBrightness,
  refreshScreen, getScreenGroups, getScreenPresets, applyPreset,
  updateScreen, sendCommandToGroup,
} from '../services/screens';
import type { Screen, ScreenGroup, ScreenPreset, ScreenMode } from '../types/screens';
import './ScreenControl.css';

interface ScreenControlProps {
  householdId: string;
  currentUserId?: string;
}

type ViewMode = 'screens' | 'groups' | 'presets';

const ScreenControl: React.FC<ScreenControlProps> = ({ householdId, currentUserId }) => {
  const [screens, setScreens] = useState<Screen[]>([]);
  const [groups, setGroups] = useState<ScreenGroup[]>([]);
  const [presets, setPresets] = useState<ScreenPreset[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('screens');
  const [selectedScreen, setSelectedScreen] = useState<Screen | null>(null);
  const [showScreenModal, setShowScreenModal] = useState(false);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [actionScreenId, setActionScreenId] = useState<string | null>(null);

  // Load data
  useEffect(() => {
    const unsubscribe = subscribeToScreenStatus(householdId, setScreens);
    getScreenGroups(householdId).then(setGroups);
    getScreenPresets(householdId).then(setPresets);
    return unsubscribe;
  }, [householdId]);

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    const [screensData, groupsData, presetsData] = await Promise.all([
      getAllScreens(householdId),
      getScreenGroups(householdId),
      getScreenPresets(householdId),
    ]);
    setScreens(screensData);
    setGroups(groupsData);
    setPresets(presetsData);
    event.detail.complete();
  };

  const handleQuickAction = async (action: string, screenId?: string) => {
    const id = screenId || actionScreenId;
    if (!id) return;

    switch (action) {
      case 'wake':
        await wakeScreen(id, currentUserId);
        break;
      case 'sleep':
        await sleepScreen(id, currentUserId);
        break;
      case 'refresh':
        await refreshScreen(id, currentUserId);
        break;
      case 'dashboard':
      case 'calendar':
      case 'photos':
      case 'meals':
      case 'chores':
        await changeScreenMode(id, action as ScreenMode, currentUserId);
        break;
    }
    setShowActionSheet(false);
  };

  const handleAllScreensAction = async (action: string) => {
    switch (action) {
      case 'wake':
        await sendCommandToAll(householdId, 'wake', {}, currentUserId);
        break;
      case 'sleep':
        await sendCommandToAll(householdId, 'sleep', {}, currentUserId);
        break;
      case 'refresh':
        await sendCommandToAll(householdId, 'refresh', {}, currentUserId);
        break;
    }
  };

  const handleApplyPreset = async (presetId: string) => {
    await applyPreset(presetId, currentUserId);
  };

  const getModeIcon = (mode: ScreenMode) => {
    const icons: Record<ScreenMode, string> = {
      dashboard: '📊',
      calendar: '📅',
      photos: '🖼️',
      meals: '🍽️',
      chores: '✅',
      groceries: '🛒',
    };
    return icons[mode] || '📺';
  };

  const getLocationIcon = (location?: string) => {
    const loc = location?.toLowerCase() || '';
    if (loc.includes('kitchen')) return cafe;
    if (loc.includes('bedroom') || loc.includes('room')) return bed;
    if (loc.includes('living')) return tv;
    return home;
  };

  const onlineScreens = screens.filter(s => s.is_online);
  const offlineScreens = screens.filter(s => !s.is_online);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Screen Control</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => handleAllScreensAction('wake')}>
              <IonIcon icon={power} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
        <IonToolbar>
          <IonSegment value={viewMode} onIonChange={e => setViewMode(e.detail.value as ViewMode)}>
            <IonSegmentButton value="screens">
              <IonIcon icon={tv} />
              <IonLabel>Screens</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="groups">
              <IonIcon icon={apps} />
              <IonLabel>Groups</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="presets">
              <IonIcon icon={sparkles} />
              <IonLabel>Presets</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>

      <IonContent className="screen-control">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        {/* SCREENS VIEW */}
        {viewMode === 'screens' && (
          <>
            {/* Quick Actions Bar */}
            <div className="quick-actions-bar">
              <IonButton size="small" onClick={() => handleAllScreensAction('wake')}>
                <IonIcon icon={sunny} slot="start" />
                Wake All
              </IonButton>
              <IonButton size="small" onClick={() => handleAllScreensAction('sleep')}>
                <IonIcon icon={moon} slot="start" />
                Sleep All
              </IonButton>
              <IonButton size="small" onClick={() => handleAllScreensAction('refresh')}>
                <IonIcon icon={refresh} slot="start" />
                Refresh All
              </IonButton>
            </div>

            {/* Online Screens */}
            <IonItemDivider>
              <IonBadge color="success" slot="start">{onlineScreens.length}</IonBadge>
              Online
            </IonItemDivider>

            <IonGrid>
              <IonRow>
                {onlineScreens.map(screen => (
                  <IonCol size="12" sizeMd="6" sizeLg="4" key={screen.id}>
                    <ScreenCard
                      screen={screen}
                      onTap={() => {
                        setActionScreenId(screen.id);
                        setShowActionSheet(true);
                      }}
                      onSettings={() => {
                        setSelectedScreen(screen);
                        setShowScreenModal(true);
                      }}
                    />
                  </IonCol>
                ))}
              </IonRow>
            </IonGrid>

            {/* Offline Screens */}
            {offlineScreens.length > 0 && (
              <>
                <IonItemDivider>
                  <IonBadge color="medium" slot="start">{offlineScreens.length}</IonBadge>
                  Offline
                </IonItemDivider>
                <IonList>
                  {offlineScreens.map(screen => (
                    <IonItem key={screen.id} className="offline-screen">
                      <IonIcon icon={getLocationIcon(screen.location)} slot="start" />
                      <IonLabel>
                        <h2>{screen.name}</h2>
                        <p>{screen.location || 'No location'}</p>
                      </IonLabel>
                      <IonBadge color="medium">Offline</IonBadge>
                    </IonItem>
                  ))}
                </IonList>
              </>
            )}
          </>
        )}

        {/* GROUPS VIEW */}
        {viewMode === 'groups' && (
          <IonList>
            <IonItemDivider>Screen Groups</IonItemDivider>
            {groups.map(group => (
              <IonItem key={group.id}>
                <span slot="start" className="group-icon">{group.icon}</span>
                <IonLabel>
                  <h2>{group.name}</h2>
                  <p>{group.screen_ids.length} screens</p>
                </IonLabel>
                <IonButton fill="clear" onClick={() => sendCommandToGroup(group.id, 'wake', {}, currentUserId)}>
                  <IonIcon icon={sunny} />
                </IonButton>
                <IonButton fill="clear" onClick={() => sendCommandToGroup(group.id, 'sleep', {}, currentUserId)}>
                  <IonIcon icon={moon} />
                </IonButton>
              </IonItem>
            ))}
            {groups.length === 0 && (
              <IonItem>
                <IonLabel className="ion-text-center">
                  <p>No groups created</p>
                </IonLabel>
              </IonItem>
            )}
            <IonButton expand="block" className="ion-margin">
              Create Group
            </IonButton>
          </IonList>
        )}

        {/* PRESETS VIEW */}
        {viewMode === 'presets' && (
          <>
            <IonList>
              <IonItemDivider>Quick Presets</IonItemDivider>
              {presets.map(preset => (
                <IonItem key={preset.id} button onClick={() => handleApplyPreset(preset.id)}>
                  <span slot="start" className="preset-icon">{preset.icon}</span>
                  <IonLabel>
                    <h2>{preset.name}</h2>
                    <p>{Object.keys(preset.screen_configs).length} screens configured</p>
                  </IonLabel>
                  <IonIcon icon={sparkles} slot="end" />
                </IonItem>
              ))}
            </IonList>

            {/* Built-in quick presets */}
            <IonCard className="ion-margin">
              <IonCardHeader>
                <IonCardTitle>Quick Actions</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonGrid>
                  <IonRow>
                    <IonCol>
                      <IonButton expand="block" onClick={() => sendCommandToAll(householdId, 'change_mode', { mode: 'dashboard' }, currentUserId)}>
                        📊 All Dashboard
                      </IonButton>
                    </IonCol>
                    <IonCol>
                      <IonButton expand="block" onClick={() => sendCommandToAll(householdId, 'change_mode', { mode: 'photos' }, currentUserId)}>
                        🖼️ All Photos
                      </IonButton>
                    </IonCol>
                  </IonRow>
                  <IonRow>
                    <IonCol>
                      <IonButton expand="block" onClick={() => sendCommandToAll(householdId, 'change_mode', { mode: 'calendar' }, currentUserId)}>
                        📅 All Calendar
                      </IonButton>
                    </IonCol>
                    <IonCol>
                      <IonButton expand="block" onClick={() => sendCommandToAll(householdId, 'change_mode', { mode: 'meals' }, currentUserId)}>
                        🍽️ All Meals
                      </IonButton>
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonCardContent>
            </IonCard>
          </>
        )}

        {/* Screen Action Sheet */}
        <IonActionSheet
          isOpen={showActionSheet}
          onDidDismiss={() => setShowActionSheet(false)}
          header="Screen Actions"
          buttons={[
            { text: '☀️ Wake', handler: () => handleQuickAction('wake') },
            { text: '🌙 Sleep', handler: () => handleQuickAction('sleep') },
            { text: '🔄 Refresh', handler: () => handleQuickAction('refresh') },
            { text: '📊 Dashboard', handler: () => handleQuickAction('dashboard') },
            { text: '📅 Calendar', handler: () => handleQuickAction('calendar') },
            { text: '🖼️ Photos', handler: () => handleQuickAction('photos') },
            { text: '🍽️ Meals', handler: () => handleQuickAction('meals') },
            { text: '✅ Chores', handler: () => handleQuickAction('chores') },
            { text: 'Cancel', role: 'cancel' },
          ]}
        />

        {/* Screen Settings Modal */}
        <ScreenSettingsModal
          screen={selectedScreen}
          isOpen={showScreenModal}
          onClose={() => setShowScreenModal(false)}
          onSave={async (updates) => {
            if (selectedScreen) {
              await updateScreen(selectedScreen.id, updates);
            }
            setShowScreenModal(false);
          }}
        />
      </IonContent>
    </IonPage>
  );
};

// ============================================
// SCREEN CARD COMPONENT
// ============================================

interface ScreenCardProps {
  screen: Screen;
  onTap: () => void;
  onSettings: () => void;
}

const ScreenCard: React.FC<ScreenCardProps> = ({ screen, onTap, onSettings }) => {
  const getModeIcon = (mode: string) => {
    const icons: Record<string, string> = {
      dashboard: '📊', calendar: '📅', photos: '🖼️',
      meals: '🍽️', chores: '✅', groceries: '🛒',
    };
    return icons[mode] || '📺';
  };

  return (
    <IonCard className={`screen-card ${screen.is_screensaver_active ? 'screensaver' : ''}`} onClick={onTap}>
      <div className="screen-preview">
        <div className="screen-mode-icon">{getModeIcon(screen.current_mode)}</div>
        {screen.is_screensaver_active && <div className="screensaver-badge">💤</div>}
      </div>
      <IonCardContent>
        <div className="screen-info">
          <h3>{screen.name}</h3>
          <p>{screen.location || 'No location'}</p>
        </div>
        <div className="screen-status">
          <IonChip color={screen.is_online ? 'success' : 'medium'}>
            {screen.is_online ? 'Online' : 'Offline'}
          </IonChip>
          <IonChip outline>{screen.current_mode}</IonChip>
        </div>
        <div className="screen-brightness">
          <IonIcon icon={sunny} />
          <span>{screen.brightness}%</span>
        </div>
        <IonButton fill="clear" size="small" onClick={(e) => { e.stopPropagation(); onSettings(); }}>
          <IonIcon icon={settings} />
        </IonButton>
      </IonCardContent>
    </IonCard>
  );
};

// ============================================
// SCREEN SETTINGS MODAL
// ============================================

interface ScreenSettingsModalProps {
  screen: Screen | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updates: Partial<Screen>) => Promise<void>;
}

const ScreenSettingsModal: React.FC<ScreenSettingsModalProps> = ({ screen, isOpen, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [defaultMode, setDefaultMode] = useState<ScreenMode>('dashboard');
  const [screensaverEnabled, setScreensaverEnabled] = useState(true);
  const [screensaverTimeout, setScreensaverTimeout] = useState(5);

  useEffect(() => {
    if (screen) {
      setName(screen.name);
      setLocation(screen.location || '');
      setDefaultMode(screen.default_mode);
      setScreensaverEnabled(screen.screensaver_enabled);
      setScreensaverTimeout(screen.screensaver_timeout_minutes);
    }
  }, [screen]);

  const handleSave = () => {
    onSave({
      name,
      location,
      default_mode: defaultMode,
      screensaver_enabled: screensaverEnabled,
      screensaver_timeout_minutes: screensaverTimeout,
    });
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Screen Settings</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>Close</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonList>
          <IonItem>
            <IonLabel position="stacked">Name</IonLabel>
            <IonInput value={name} onIonChange={e => setName(e.detail.value || '')} />
          </IonItem>
          <IonItem>
            <IonLabel position="stacked">Location</IonLabel>
            <IonInput value={location} onIonChange={e => setLocation(e.detail.value || '')} placeholder="e.g., Kitchen, Living Room" />
          </IonItem>
          <IonItem>
            <IonLabel>Default Mode</IonLabel>
            <IonSelect value={defaultMode} onIonChange={e => setDefaultMode(e.detail.value)}>
              <IonSelectOption value="dashboard">📊 Dashboard</IonSelectOption>
              <IonSelectOption value="calendar">📅 Calendar</IonSelectOption>
              <IonSelectOption value="photos">🖼️ Photos</IonSelectOption>
              <IonSelectOption value="meals">🍽️ Meals</IonSelectOption>
              <IonSelectOption value="chores">✅ Chores</IonSelectOption>
            </IonSelect>
          </IonItem>
          <IonItem>
            <IonLabel>Screensaver</IonLabel>
            <IonToggle checked={screensaverEnabled} onIonChange={e => setScreensaverEnabled(e.detail.checked)} />
          </IonItem>
          <IonItem>
            <IonLabel>
              <h2>Timeout</h2>
              <p>{screensaverTimeout} minutes</p>
            </IonLabel>
            <IonRange min={1} max={30} value={screensaverTimeout} onIonChange={e => setScreensaverTimeout(e.detail.value as number)} />
          </IonItem>
        </IonList>
        <IonButton expand="block" className="ion-margin-top" onClick={handleSave}>
          Save Changes
        </IonButton>
      </IonContent>
    </IonModal>
  );
};

export default ScreenControl;
